import {
  Box,
  InputAdornment,
  Paper,
  SvgIcon,
  TextField,
  makeStyles,
  withStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useQuery } from '../hooks/useQuery';
import { useNavigate } from 'react-router';
import axiosInterceptor from 'src/app/axiosInterceptor';
import { getVehicleLicensePlate } from 'src/features/vehicleSlice';

const useStyles = makeStyles(theme => ({
  root: {
    width: 365,
    boxShadow: 'none',
    padding: '0 13px 0 0'
  },
  input: {
    flex: 1,
    fontSize: '14px',
    padding: '0 !important',
    paddingLeft: '8px !important'
  },
  inputRoot: {
    height: 40,
    display: 'flex',
    alignItems: 'center',

    '& .MuiOutlinedInput-notchedOutline': {
      top: 0,
    },
  },
  option: {
    fontSize: '14px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',

    '&:last-child': {
      borderBottom: 'none'
    }
  },
  customOutline: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#D6D9DB',
        borderWidth: 1
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F'
      }
    }
  }
}));

const CssTextField = withStyles({
  root: {
    '& .MuiInput-underline:after': {
      borderBottomColor: '#D6D9DB',
    },

    '& .MuiOutlinedInput-root': {
      height: 40,
      display: 'flex',
      alignItems: 'center',

      '& fieldset': {
        borderColor: '#D6D9DB',
      },
      '&:hover fieldset': {
        borderColor: '#D9042F',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F',
      },
    },

    '& .MuiOutlinedInput-input': {
      padding: 0,
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
    },

    '& .MuiInputAdornment-root': {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
    },
  },
})(TextField)

export default function ToolBar() {
  const classes = useStyles();
  const query = useQuery();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isAdmin, isAgency, isUser, agencyId: authAgencyId, isDriver } = useSelector(state => state.authSlice);

  const searchAgencyIdFromUrl = query.get('search_agency_id') || '';
  const searchEndUserIdFromUrl = query.get('search_end_user_id') || '';
  const licensePlateFromUrl = query.get('license_plate') || '';

  const [agencyOptions, setAgencyOptions] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyInputValue, setAgencyInputValue] = useState('');
  const [lpSearchValue, setLpSearchValue] = useState(licensePlateFromUrl);
  const [loading, setLoading] = useState(false);

  const listLicensePlate = useSelector(state => state.vehicleSlice.listLicensePlate);

  const debounceRef = useRef(null);

  const searchTypes = [
    { id: 'agency', name: 'Đại lý' },
    { id: 'enduser', name: 'Người dùng' },
    { id: 'vehicle', name: 'Xe lẻ' }
  ];

  useEffect(() => {
    setLpSearchValue(licensePlateFromUrl);
  }, [licensePlateFromUrl]);

  useEffect(() => {
    if (!searchAgencyIdFromUrl && !searchEndUserIdFromUrl) {
      setSelectedAgency(null);
      setAgencyInputValue('');
      return;
    }

    if (searchAgencyIdFromUrl) {
      if (selectedAgency && String(selectedAgency.id) === String(searchAgencyIdFromUrl) && selectedAgency.type === 'agency') return;
      axiosInterceptor
        .get(`/agencies/${searchAgencyIdFromUrl}`)
        .then(res => {
          const raw = res?.data?.payload;
          const a = raw?.agency ?? raw;
          if (a?.id) {
            setSelectedAgency({ id: a.id, name: a.name, type: 'agency' });
            setAgencyInputValue(a.name);
          }
        })
        .catch(() => { });
    } else if (searchEndUserIdFromUrl) {
      if (selectedAgency && String(selectedAgency.id) === String(searchEndUserIdFromUrl) && selectedAgency.type === 'enduser') return;

      axiosInterceptor
        .get(`/users/info/${searchEndUserIdFromUrl}`)
        .then(res => {
          const u = res?.data?.payload?.user || res?.data?.payload;
          if (u?.id || u?.end_user_id) {
            const name = u.full_name || u.name || u.username;
            setSelectedAgency({ id: u.end_user_id || u.id, name, type: 'enduser' });
            setAgencyInputValue(name);
          }
        })
        .catch(() => {

        });
    }
  }, [searchAgencyIdFromUrl, searchEndUserIdFromUrl, authAgencyId]);

  useEffect(() => {
    if (isAdmin) return;
    if (isAgency) {
      dispatch(getVehicleLicensePlate(`/${authAgencyId}`));
    } else if (isUser || isDriver) {
      dispatch(getVehicleLicensePlate(''));
    }
  }, [isAdmin, isAgency, isUser, isDriver, authAgencyId, dispatch]);

  const searchAgencies = useCallback((keywords) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!keywords || keywords.trim().length === 0) {
      setAgencyOptions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        let results = [];

        if (isAdmin) {
          const res = await axiosInterceptor.get(`/agencies?keywords=${encodeURIComponent(keywords.trim())}&page_size=1000`);
          const agencyData = res?.data?.payload?.data || res?.data?.payload?.agencies || res?.data?.payload || [];
          results = Array.isArray(agencyData)
            ? agencyData.map(a => ({ id: a.id, name: a.name, type: 'agency' }))
            : [];
        } else if (isAgency) {
          const res = await axiosInterceptor.get(`/agencies/${authAgencyId}/enduser?keywords=${encodeURIComponent(keywords.trim())}&page_size=1000`);
          const userData = res?.data?.payload?.users || res?.data?.payload?.data || [];
          results = Array.isArray(userData)
            ? userData.map(u => ({
              id: u.end_user_id || u.id,
              name: u.full_name || u.name || u.username,
              type: 'enduser',
              agency_id: u.agency_id || authAgencyId
            }))
            : [];
        }

        setAgencyOptions(results);
      } catch (err) {
        setAgencyOptions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [isAgency, authAgencyId]);

  const handleAgencyInputChange = (e, value, reason) => {
    setAgencyInputValue(value);
    if (reason === 'input') {
      searchAgencies(value);
    }
    if (reason === 'clear') {
      setAgencyOptions([]);
    }
  };

  const handleChangeAgency = (e, value) => {
    setSelectedAgency(value || null);
    setAgencyOptions([]);

    const params = new URLSearchParams(window.location.search);
    if (value) {
      if (value.type === 'agency') {
        params.set('agency_id', value.id);
        params.set('search_agency_id', value.id);
        params.delete('end_user_id');
        params.delete('search_end_user_id');
      } else if (value.type === 'enduser') {
        params.set('end_user_id', value.id);
        params.set('search_end_user_id', value.id);

        if (value.agency_id) {
          params.set('agency_id', value.agency_id);

        } else if (isAgency) {
          params.set('agency_id', authAgencyId);
        }
      }
    } else {
      params.delete('agency_id');
      params.delete('search_agency_id');
      params.delete('end_user_id');
      params.delete('search_end_user_id');
    }

    const search = params.toString();
    navigate(search ? `/app/tracking?${search}` : '/app/tracking');
  };

  const handleLpSearch = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const params = new URLSearchParams(window.location.search);
      if (lpSearchValue && lpSearchValue.trim()) {
        params.set('license_plate', lpSearchValue.trim().toUpperCase());
      } else {
        params.delete('license_plate');
      }
      const search = params.toString();
      navigate(search ? `/app/tracking?${search}` : '/app/tracking');
    }
  };

  const handleChangeLp = (e, value) => {
    const newVal = value || '';
    setLpSearchValue(newVal);
    const params = new URLSearchParams(window.location.search);
    if (newVal.trim()) {
      params.set('license_plate', newVal.trim().toUpperCase());
    } else {
      params.delete('license_plate');
    }
    const search = params.toString();
    navigate(search ? `/app/tracking?${search}` : '/app/tracking');
  };

  return (
    <Paper component="form" className={classes.root} style={{ marginTop: 0 }} onSubmit={e => e.preventDefault()}>
      {(isUser || isDriver) && (
        <Box mb={0}>
          <Autocomplete
            freeSolo
            id="license-plate-search"
            size="small"
            options={listLicensePlate || []}
            value={lpSearchValue}
            onChange={handleChangeLp}
            onInputChange={(e, val) => setLpSearchValue(val)}
            renderInput={params => (
              <CssTextField
                {...params}
                fullWidth
                variant="outlined"
                placeholder="Tìm biển số xe..."
                onKeyDown={handleLpSearch}
                InputProps={{
                  ...params.InputProps,
                  style: { fontSize: '14px', height: '40px' },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon fontSize="small" color="action">
                        <SearchIcon />
                      </SvgIcon>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
        </Box>
      )}

      {(isAdmin || isAgency) && (
        <Box display="flex" width="100%">
          <Box flex={1} mr={0}>
            <Autocomplete
              id="agency-search-autocomplete"
              size="small"
              options={agencyOptions}
              forcePopupIcon={false}
              getOptionLabel={option => option.name || ''}
              value={selectedAgency}
              inputValue={agencyInputValue}
              onInputChange={handleAgencyInputChange}
              onChange={handleChangeAgency}
              loading={loading}
              loadingText="Đang tìm..."
              noOptionsText={agencyInputValue.trim() ? (isAdmin ? 'Không tìm thấy đại lý' : 'Không tìm thấy đại lý/người dùng') : 'Nhập để tìm kiếm'}
              filterOptions={x => x}
              getOptionSelected={(option, value) => option.id === value.id && option.type === value.type}
              classes={{ option: classes.option }}
              renderOption={option => (
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box>{option.name}</Box>
                  <Box fontSize="11px" color="textSecondary" ml={1}>
                    {option.type === 'agency' ? 'Đại lý' : 'Người dùng'}
                  </Box>
                </Box>
              )}
              renderInput={params => (
                <CssTextField
                  {...params}
                  variant="outlined"
                  placeholder="Tìm kiếm đại lý/người dùng"
                  InputProps={{
                    ...params.InputProps,
                    style: { fontSize: '14px', height: '36px' }
                  }}
                />
              )}
            />
          </Box>
        </Box>
      )}
      
    </Paper>
  );
}